/**
 * adapters.ts - Data adapters for converting various data sources to GraphData
 * 
 * CHANGE HISTORY:
 * - 251031_2115 [pending] danielbeer: Created adapters for Aggregatr integration
 * 
 * Provides utilities to convert data from Aggregatr and other sources
 * into the GraphData format required by graph visualization components.
 */

import type { GraphData, GraphNode, GraphEdge } from './types';

/**
 * Configuration for converting generic data to graph format
 */
export interface GraphDataConfig<T> {
  // Node configuration
  nodeIdField: keyof T | ((item: T) => string);
  nodeLabelField: keyof T | ((item: T) => string);
  nodeGroupField?: keyof T | ((item: T) => string | undefined);
  nodeSizeField?: keyof T | ((item: T) => number | undefined);
  nodeColorField?: keyof T | ((item: T) => string | undefined);
  
  // Edge configuration
  edgeSourceField?: keyof T | ((item: T) => string | string[] | undefined);
  edgeTargetField?: keyof T | ((item: T) => string | string[] | undefined);
  edgeTypeField?: keyof T | ((item: T) => string | undefined);
  
  // Alternative: provide edges separately
  edges?: Array<{
    source: string;
    target: string;
    type?: string;
    label?: string;
    color?: string;
    weight?: number;
  }>;
  
  // Custom metadata extractor
  metadataExtractor?: (item: T) => Record<string, any>;
}

/**
 * Convert generic data array to GraphData format
 * 
 * Works with Aggregatr CRUD results, API responses, or any data array
 * 
 * @example
 * ```typescript
 * // From Aggregatr
 * const items = await crud.list();
 * const graphData = convertToGraphData(items, {
 *   nodeIdField: 'id',
 *   nodeLabelField: 'name',
 *   nodeGroupField: 'category',
 *   edgeSourceField: 'id',
 *   edgeTargetField: 'dependencies', // array of IDs
 * });
 * ```
 */
export function convertToGraphData<T>(
  data: T[],
  config: GraphDataConfig<T>
): GraphData<T> {
  const getValue = <K>(item: T, field: keyof T | ((item: T) => K)): K => {
    return typeof field === 'function' ? field(item) : (item[field] as unknown as K);
  };

  // Create nodes
  const nodes: GraphNode<T>[] = data.map(item => {
    const node: GraphNode<T> = {
      id: getValue(item, config.nodeIdField),
      label: getValue(item, config.nodeLabelField),
      data: item,
    };

    if (config.nodeGroupField) {
      const group = getValue(item, config.nodeGroupField);
      if (group !== undefined) node.group = group;
    }

    if (config.nodeSizeField) {
      const size = getValue(item, config.nodeSizeField);
      if (size !== undefined) node.size = size;
    }

    if (config.nodeColorField) {
      const color = getValue(item, config.nodeColorField);
      if (color !== undefined) node.color = color;
    }

    if (config.metadataExtractor) {
      node.metadata = config.metadataExtractor(item);
    }

    return node;
  });

  // Create edges
  let edges: GraphEdge[] = [];

  if (config.edges) {
    // Use provided edges
    edges = config.edges.map(edge => ({
      id: edge.source && edge.target ? `${edge.source}-${edge.target}` : undefined,
      source: edge.source,
      target: edge.target,
      type: edge.type,
      label: edge.label,
      color: edge.color,
      weight: edge.weight,
    }));
  } else if (config.edgeSourceField && config.edgeTargetField) {
    // Extract edges from data
    data.forEach(item => {
      const source = getValue(item, config.edgeSourceField!);
      const targets = getValue(item, config.edgeTargetField!);
      const type = config.edgeTypeField ? getValue(item, config.edgeTypeField) : undefined;

      if (!source) return;

      // Handle single target or array of targets
      const targetArray = Array.isArray(targets) ? targets : targets ? [targets] : [];

      targetArray.forEach(target => {
        if (target) {
          edges.push({
            id: `${source}-${target}`,
            source: String(source),
            target: String(target),
            type,
          });
        }
      });
    });
  }

  return { nodes, edges };
}

/**
 * Convert Aggregatr CRUD Manager results to GraphData
 * 
 * @example
 * ```typescript
 * import { CRUDManager } from '@opcentral/aggregatr';
 * import { fromAggregatr } from '@opcentral/viewr';
 * 
 * const crud = new CRUDManager({ storeName: 'modules' });
 * const modules = await crud.list();
 * 
 * const graphData = fromAggregatr(modules, {
 *   nodeIdField: 'name',
 *   nodeLabelField: 'displayName',
 *   nodeGroupField: 'category',
 *   edgeSourceField: 'name',
 *   edgeTargetField: 'dependencies',
 * });
 * 
 * <GraphViewer data={graphData} />
 * ```
 */
export function fromAggregatr<T>(
  data: T[],
  config: GraphDataConfig<T>
): GraphData<T> {
  return convertToGraphData(data, config);
}

/**
 * Convert relational data with separate nodes and edges arrays
 * 
 * @example
 * ```typescript
 * const graphData = fromRelationalData(
 *   nodes: [{ id: 1, name: 'A' }, { id: 2, name: 'B' }],
 *   edges: [{ from: 1, to: 2, type: 'depends' }],
 *   {
 *     nodeIdField: 'id',
 *     nodeLabelField: 'name',
 *     edgeSourceField: 'from',
 *     edgeTargetField: 'to',
 *     edgeTypeField: 'type',
 *   }
 * );
 * ```
 */
export function fromRelationalData<TNode, TEdge>(
  nodes: TNode[],
  edges: TEdge[],
  config: {
    nodeIdField: keyof TNode | ((item: TNode) => string);
    nodeLabelField: keyof TNode | ((item: TNode) => string);
    nodeGroupField?: keyof TNode | ((item: TNode) => string | undefined);
    nodeSizeField?: keyof TNode | ((item: TNode) => number | undefined);
    nodeColorField?: keyof TNode | ((item: TNode) => string | undefined);
    edgeSourceField: keyof TEdge | ((item: TEdge) => string);
    edgeTargetField: keyof TEdge | ((item: TEdge) => string);
    edgeTypeField?: keyof TEdge | ((item: TEdge) => string | undefined);
    edgeLabelField?: keyof TEdge | ((item: TEdge) => string | undefined);
    edgeColorField?: keyof TEdge | ((item: TEdge) => string | undefined);
    edgeWeightField?: keyof TEdge | ((item: TEdge) => number | undefined);
    metadataExtractor?: (item: TNode) => Record<string, any>;
  }
): GraphData<TNode> {
  const getValue = <T, K>(item: T, field: keyof T | ((item: T) => K)): K => {
    return typeof field === 'function' ? field(item) : (item[field] as unknown as K);
  };

  // Create nodes
  const graphNodes: GraphNode<TNode>[] = nodes.map(item => {
    const node: GraphNode<TNode> = {
      id: getValue(item, config.nodeIdField),
      label: getValue(item, config.nodeLabelField),
      data: item,
    };

    if (config.nodeGroupField) {
      const group = getValue(item, config.nodeGroupField);
      if (group !== undefined) node.group = group;
    }

    if (config.nodeSizeField) {
      const size = getValue(item, config.nodeSizeField);
      if (size !== undefined) node.size = size;
    }

    if (config.nodeColorField) {
      const color = getValue(item, config.nodeColorField);
      if (color !== undefined) node.color = color;
    }

    if (config.metadataExtractor) {
      node.metadata = config.metadataExtractor(item);
    }

    return node;
  });

  // Create edges
  const graphEdges: GraphEdge[] = edges.map(item => {
    const edge: GraphEdge = {
      source: getValue(item, config.edgeSourceField),
      target: getValue(item, config.edgeTargetField),
    };

    if (config.edgeTypeField) {
      const type = getValue(item, config.edgeTypeField);
      if (type !== undefined) edge.type = type;
    }

    if (config.edgeLabelField) {
      const label = getValue(item, config.edgeLabelField);
      if (label !== undefined) edge.label = label;
    }

    if (config.edgeColorField) {
      const color = getValue(item, config.edgeColorField);
      if (color !== undefined) edge.color = color;
    }

    if (config.edgeWeightField) {
      const weight = getValue(item, config.edgeWeightField);
      if (weight !== undefined) edge.weight = weight;
    }

    edge.id = `${edge.source}-${edge.target}`;

    return edge;
  });

  return { nodes: graphNodes, edges: graphEdges };
}

/**
 * Convert hierarchical/tree data to GraphData
 * 
 * @example
 * ```typescript
 * const treeData = {
 *   id: 'root',
 *   name: 'Root',
 *   children: [
 *     { id: 'a', name: 'A', children: [] },
 *     { id: 'b', name: 'B', children: [] },
 *   ]
 * };
 * 
 * const graphData = fromHierarchicalData(treeData, {
 *   idField: 'id',
 *   labelField: 'name',
 *   childrenField: 'children',
 * });
 * ```
 */
export function fromHierarchicalData<T extends Record<string, any>>(
  root: T,
  config: {
    idField: keyof T;
    labelField: keyof T;
    childrenField: keyof T;
    groupField?: keyof T;
    sizeField?: keyof T;
    colorField?: keyof T;
  }
): GraphData<T> {
  const nodes: GraphNode<T>[] = [];
  const edges: GraphEdge[] = [];

  function traverse(item: T, parent?: T) {
    const node: GraphNode<T> = {
      id: String(item[config.idField]),
      label: String(item[config.labelField]),
      data: item,
    };

    if (config.groupField && item[config.groupField] !== undefined) {
      node.group = String(item[config.groupField]);
    }

    if (config.sizeField && item[config.sizeField] !== undefined) {
      node.size = Number(item[config.sizeField]);
    }

    if (config.colorField && item[config.colorField] !== undefined) {
      node.color = String(item[config.colorField]);
    }

    nodes.push(node);

    if (parent) {
      edges.push({
        id: `${parent[config.idField]}-${item[config.idField]}`,
        source: String(parent[config.idField]),
        target: String(item[config.idField]),
        type: 'parent-child',
      });
    }

    const children = item[config.childrenField];
    if (Array.isArray(children)) {
      children.forEach((child: T) => traverse(child, item));
    }
  }

  traverse(root);

  return { nodes, edges };
}

/**
 * Merge multiple GraphData sources
 * 
 * @example
 * ```typescript
 * const combined = mergeGraphData([graphData1, graphData2, graphData3]);
 * ```
 */
export function mergeGraphData<T>(...graphs: GraphData<T>[]): GraphData<T> {
  const nodeMap = new Map<string, GraphNode<T>>();
  const edgeMap = new Map<string, GraphEdge>();

  graphs.forEach(graph => {
    graph.nodes.forEach(node => {
      nodeMap.set(node.id, node);
    });

    graph.edges.forEach(edge => {
      const key = edge.id || `${edge.source}-${edge.target}`;
      edgeMap.set(key, edge);
    });
  });

  return {
    nodes: Array.from(nodeMap.values()),
    edges: Array.from(edgeMap.values()),
  };
}

/**
 * Filter GraphData based on predicate functions
 * 
 * @example
 * ```typescript
 * const filtered = filterGraphData(graphData, {
 *   nodes: (node) => node.data.status === 'active',
 *   edges: (edge) => edge.type === 'required',
 * });
 * ```
 */
export function filterGraphData<T>(
  graph: GraphData<T>,
  filters: {
    nodes?: (node: GraphNode<T>) => boolean;
    edges?: (edge: GraphEdge) => boolean;
  }
): GraphData<T> {
  const filteredNodes = filters.nodes ? graph.nodes.filter(filters.nodes) : graph.nodes;
  const nodeIds = new Set(filteredNodes.map(n => n.id));

  // Only include edges where both source and target exist in filtered nodes
  let filteredEdges = graph.edges.filter(
    edge => nodeIds.has(edge.source) && nodeIds.has(edge.target)
  );

  if (filters.edges) {
    filteredEdges = filteredEdges.filter(filters.edges);
  }

  return {
    nodes: filteredNodes,
    edges: filteredEdges,
  };
}
